import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import stripeLogo from "../../../assets/stripelogo.png";
import successIcon from "../../../assets/success-icon.svg";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import Swal from "sweetalert2";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { API_URL, createAxiosInstance } from "../../../api";
import "./Step0.css";
import {ToDashboardButton} from "../../Dashboard/Components/ToDashboardButton";
import config from "config";


const Step3 = (props) => {
  const { goBack, save, loading } = props;
  const stripe = useStripe();
  const elements = useElements();
  const [paid, setPaid] = useState(false); // Initialize paid state
  const [paidStatus, setPaidStatus] = useState("false"); // Initialize paid status
  const [lastName, setLastName] = useState(""); // State for last name
  const [firstName, setFirstName] = useState(""); // State for first name
  const [subscriptionType, setSubscriptionType] = useState("monthly");
  const [paymentLoading, setPaymentLoading] = useState(false);

  const cardElementStyles = {
    base: {
      backgroundColor: "light-grey",
      fontSize: "18px",
      color: "#32325d",
      "::placeholder": {
        color: "#aab7c4",
      },
      border: "1px solid #ccc",
      borderRadius: "4px",
      padding: "10px",
      marginBottom: "16px",
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
    focus: {
      borderColor: "#712d93",
    },
  };

  const userId = useSelector((state) => state.profile.user.user);

  const handlePayment = async () => {
    if (!stripe || !elements) {
      Swal.fire({
        icon: "error",
        title: "Payment System Loading",
        text: "Please wait while the payment system initializes",
      });
      return;
    }
    setPaymentLoading(true);

    const cardNumberElement = elements.getElement(CardNumberElement);

    if (!cardNumberElement) {
      Swal.fire({
        icon: "error",
        title: "Payment Failed",
        text: "Card element is not available.",
      });
      setPaymentLoading(false);
      return;
    }

    try {
      const { error, token } = await stripe.createToken(cardNumberElement);

      if (error) {
        Swal.fire({
          icon: "error",
          title: "Payment Failed",
          text: error.message,
        });
        setPaymentLoading(false);
        return;
      }

      const axiosInstance = createAxiosInstance();

      if (!firstName.trim() || !lastName.trim()) {
        Swal.fire({
          icon: "error",
          title: "Missing Information",
          text: "Please enter both first and last name",
        });
        setPaymentLoading(false);
        return;
      }
      const response = await axiosInstance.post(
        `${API_URL}/api/billings/stripe`,
        {
          token: token.id,
          subscriptionType,
          name: `${firstName} ${lastName}`,
          userId,
        }
      );

      if (response.data && response.data.success) {
        Swal.fire({
          imageUrl: successIcon,
          imageHeight: "100px",
          title: "Payment Successful",
          text: "Your subscription has been activated!",
          confirmButtonColor: "#712d94",
          customClass: {
            icon: "no-before-icon",
          },
        }).then(() => {
          setPaid(true);
          setPaidStatus("Paid");
          handleNext(); // Ensure this is called after the user closes the popup
        });
      } else {
        throw new Error(response.data.message || "Payment failed");
      }
    } catch (error) {
      console.error("Payment processing error:", error);
      Swal.fire({
        icon: "error",
        title: "Payment Failed",
        text:
          error.response?.data?.message ||
          "There was an issue processing your payment.",
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!userId) {
        console.log("User ID is undefined or invalid. Skipping payment check.");
        return; // Exit the function early if the userId is not valid
      }

      try {

        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.post(
          `${API_URL}/api/billings/hasPaid/${userId}`
        );

        // Handle string values "Paid" and "Not Paid" from the API
        const paidStatus = response.data?.paid;
        const isPaid = paidStatus === "Paid" || paidStatus === true;
        
        if (response.data && isPaid) {
          setPaid(true);
          setPaidStatus(response.data.paid);
        } else {
          setPaid(false);
          setPaidStatus("false");
        }
      } catch (error) {
        console.error(
          "Error checking payment status:",
          error.response?.data?.message || error.message
        );
        setPaidStatus("false");
      }
    };

    checkPaymentStatus();
  }, [userId]); // Re-run the effect when `userId` changes

  const handleNext = () => {
    save();
  };

  const back = () => {
    goBack();
  };

  return (
      <Box
          sx={{ p: 2 }}
      >
      <Grid container spacing={2}>
        <Grid
            className="back-to-dashboard"
            sx={{
              textAlign: "right",
              justifyContent: { xs: "start", md: "end" },
              minHeight: "100%",
              display: "flex",
              alignItems: "center",
            }}
            item
            xs={1}
            sm={1}
            md={3}
        >
          <ToDashboardButton />
      </Grid>
        <Grid item xs={11} sm={10} md={6} style={{ textAlign: "center" }}>
          <Box sx={{ mb: 2 }}>
            <p
                className={`step-title ${config["primary-text-colour-class"]}`}
                style={{ lineHeight: "30px" }}
            >
              <span>Choose Your Subscription Plan</span>
              <br />
            </p>
          </Box>
        </Grid>
        </Grid>
        
          <Box
              component="form"
              noValidate
              autoComplete="off"
              className="boxy main"
              >
    
          <Typography variant="body1" align="center">
            “You can cancel at any time”
          </Typography>
    
          <Typography variant="body1" align="center" style={{ marginTop: 10 }}>
            “Cheaper than a cup of coffee!”
          </Typography>
    
          <Grid container spacing={2} justifyContent="center" sx={{ mt: 5 }}>
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                fullWidth
                className="memberoptions"
                color={subscriptionType === "monthly" ? "primary" : "inherit"}
                onClick={() => setSubscriptionType("monthly")}
              >
                Monthly - £2.00
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                fullWidth
                className="memberoptions"
                color={subscriptionType === "yearly" ? "primary" : "inherit"}
                onClick={() => setSubscriptionType("yearly")}
              >
                Yearly - £24.99
              </Button>
            </Grid>
          </Grid>
    
          <Typography
            variant="h5"
            align="center"
            sx={{ my: 2 }}
            style={{ marginTop: "50px" }}
          >
            Payment Details
          </Typography>
          <div className="stripe-input-container">
            <Grid
              container
              spacing={2}
              justifyContent="center"
              className="input-names"
            >
              <Grid
                container
                spacing={2}
                style={{ position: "relative", left: "8px" }}
              >
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="First Name"
                    variant="outlined"
                    fullWidth
                    className="input-names"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Last Name"
                    variant="outlined"
                    fullWidth
                    className="input-names"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Grid>
              </Grid>
    
              <Grid item xs={12}>
                <CardNumberElement options={{ style: cardElementStyles }} />
              </Grid>
    
              <Grid item xs={12} sm={6}>
                <CardExpiryElement options={{ style: cardElementStyles }} />
              </Grid>
    
              <Grid item xs={12} sm={6}>
                <CardCvcElement options={{ style: cardElementStyles }} />
              </Grid>
            </Grid>
    
            <LoadingButton
              variant="contained"
              color="primary"
              onClick={handlePayment}
              loading={paymentLoading}
              disabled={!stripe || paymentLoading} // Add this
            >
              Pay Now
            </LoadingButton>
          </div>
          <span className="button-box" style={{ maxWidth: "800px" }}>
            <Button type="button" variant="outlined" size="large" onClick={back}>
              Back
            </Button>
    
            <div>
              {paidStatus !== "false" && (
                <Button
                  type="button"
                  variant="contained"
                  size="large"
                  onClick={handleNext}
                >
                  {loading && (
                    <CircularProgress
                      size="20px"
                      style={{ marginRight: "10px", color: "white" }}
                    />
                  )}
                  Next
                </Button>
              )}
            </div>
          </span>
          <img className="stripelogo" src={stripeLogo} alt="Powered by" />
        </Box>
      </Box>
  );
};

export default Step3;
